import api from './api';

export interface UnitDisplay {
  model: boolean;
  collection: boolean;
  unitType: boolean;
  bathroom: boolean;
  bedroom: boolean;
  den: boolean;
  media: boolean;
  flex: boolean;
  area: boolean;
  ceiling: boolean;
  outdoorArea: boolean;
  outdoorType: boolean;
  parking: boolean;
  locker: boolean;
  bicycle: boolean;
}

export interface UnitOption {
  floorLayout: boolean;
  keyMap: boolean;
  electricalLayout: boolean;
  suiteLayout: boolean;
  photos: boolean;
}

export interface UnitSummary {
  model: boolean;
  collection: boolean;
  unitType: boolean;
  bathroom: boolean;
  bedroom: boolean;
  den: boolean;
  media: boolean;
  flex: boolean;
  area: boolean;
  ceiling: boolean;
  outdoorArea: boolean;
  outdoorType: boolean;
  floorMarketing: boolean;
  views: boolean;
}

export interface PortalSettings {
  _id?: string;
  workspaceId: string;
  // General - Branding
  primaryColor: string;
  headerLogo?: string;
  loginPageLogo?: string;
  loginPageHeroImage?: string;
  loginPageVideoUrl?: string;
  loginPageVideoType?: 'youtube' | 'vimeo' | 'mp4';
  bannerImage?: string;
  introText: string;
  // Content Settings
  extraInfoTitle: string;
  extraInfoBody: string;
  helpText: string;
  // Image Disclaimer
  showImageDisclaimer: boolean;
  imageDisclaimerText: string;
  // Units
  unitDisplay?: UnitDisplay;
  unitOption?: UnitOption;
  unitSummary?: UnitSummary;
  // Parking
  parkingSelectionTimeLimit: number;
  parkingInfoText: string;
  lockerInfoText: string;
  bicycleInfoText: string;
  // Incentives / Promotions
  displayIncentivesOnHomePage: boolean;
  displayIncentivesOnUnitDetails: boolean;
  incentiveTitle: string;
  incentiveBody: string;
  incentiveImage?: string;
  // Payments
  showPaymentInstructions: boolean;
  paymentInstructions: string;
  // Others
  showDesignStudioSubmitInstructions: boolean;
  designStudioSubmitInstructions: string;
  showLayoutCustomizationSubmitInstructions: boolean;
  layoutCustomizationSubmitInstructions: string;
  homeOwnersGuideProjects: string[];
  colorSelectionProjects: string[];
  amenitiesButtonProjects: string[];
  projectProgressButtonProjects: string[];
  closingGuideButtonProjects: Array<{ projectId: string; url: string }>;
}

export interface PortalSettingsFormData {
  primaryColor: string;
  headerLogo?: File;
  loginPageLogo?: File;
  loginPageHeroImage?: File;
  loginPageVideoUrl?: string;
  loginPageVideoType?: 'youtube' | 'vimeo' | 'mp4';
  bannerImage?: File;
  introText: string;
  extraInfoTitle: string;
  extraInfoBody: string;
  helpText: string;
  showImageDisclaimer?: boolean;
  imageDisclaimerText?: string;
  unitDisplay?: UnitDisplay;
  unitOption?: UnitOption;
  unitSummary?: UnitSummary;
  parkingSelectionTimeLimit?: number;
  parkingInfoText?: string;
  lockerInfoText?: string;
  bicycleInfoText?: string;
  displayIncentivesOnHomePage?: boolean;
  displayIncentivesOnUnitDetails?: boolean;
  incentiveTitle?: string;
  incentiveBody?: string;
  incentiveImage?: File;
  showPaymentInstructions?: boolean;
  paymentInstructions?: string;
  showDesignStudioSubmitInstructions?: boolean;
  designStudioSubmitInstructions?: string;
  showLayoutCustomizationSubmitInstructions?: boolean;
  layoutCustomizationSubmitInstructions?: string;
  homeOwnersGuideProjects?: string[];
  colorSelectionProjects?: string[];
  amenitiesButtonProjects?: string[];
  projectProgressButtonProjects?: string[];
  closingGuideButtonProjects?: Array<{ projectId: string; url: string }>;
  imagesToDelete?: string[];
}

export const portalSettingsService = {
  // Get portal settings for a workspace
  get: async (workspaceId: string): Promise<PortalSettings> => {
    const response = await api.get(`/workspaces/${workspaceId}/portal-settings`);
    return response.data;
  },

  // Create or update portal settings
  save: async (
    workspaceId: string,
    data: Partial<PortalSettingsFormData>
  ): Promise<PortalSettings> => {
    const formData = new FormData();

    // Add text fields only if provided
    if (data.primaryColor !== undefined) {
      formData.append('primaryColor', data.primaryColor);
    }
    if (data.introText !== undefined) {
      formData.append('introText', data.introText);
    }
    if (data.extraInfoTitle !== undefined) {
      formData.append('extraInfoTitle', data.extraInfoTitle);
    }
    if (data.extraInfoBody !== undefined) {
      formData.append('extraInfoBody', data.extraInfoBody);
    }
    if (data.helpText !== undefined) {
      formData.append('helpText', data.helpText);
    }
    if (data.showImageDisclaimer !== undefined) {
      formData.append('showImageDisclaimer', String(data.showImageDisclaimer));
    }
    if (data.imageDisclaimerText !== undefined) {
      formData.append('imageDisclaimerText', data.imageDisclaimerText);
    }
    if (data.loginPageVideoUrl !== undefined) {
      formData.append('loginPageVideoUrl', data.loginPageVideoUrl);
    }
    if (data.loginPageVideoType !== undefined) {
      formData.append('loginPageVideoType', data.loginPageVideoType);
    }

    // Add nested objects as JSON strings
    if (data.unitDisplay !== undefined) {
      formData.append('unitDisplay', JSON.stringify(data.unitDisplay));
    }
    if (data.unitOption !== undefined) {
      formData.append('unitOption', JSON.stringify(data.unitOption));
    }
    if (data.unitSummary !== undefined) {
      formData.append('unitSummary', JSON.stringify(data.unitSummary));
    }

    // Add parking fields
    if (data.parkingSelectionTimeLimit !== undefined) {
      formData.append('parkingSelectionTimeLimit', String(data.parkingSelectionTimeLimit));
    }
    if (data.parkingInfoText !== undefined) {
      formData.append('parkingInfoText', data.parkingInfoText);
    }
    if (data.lockerInfoText !== undefined) {
      formData.append('lockerInfoText', data.lockerInfoText);
    }
    if (data.bicycleInfoText !== undefined) {
      formData.append('bicycleInfoText', data.bicycleInfoText);
    }

    // Add incentives fields
    if (data.displayIncentivesOnHomePage !== undefined) {
      formData.append('displayIncentivesOnHomePage', String(data.displayIncentivesOnHomePage));
    }
    if (data.displayIncentivesOnUnitDetails !== undefined) {
      formData.append('displayIncentivesOnUnitDetails', String(data.displayIncentivesOnUnitDetails));
    }
    if (data.incentiveTitle !== undefined) {
      formData.append('incentiveTitle', data.incentiveTitle);
    }
    if (data.incentiveBody !== undefined) {
      formData.append('incentiveBody', data.incentiveBody);
    }

    if (data.showPaymentInstructions !== undefined) {
      formData.append('showPaymentInstructions', String(data.showPaymentInstructions));
    }
    if (data.paymentInstructions !== undefined) {
      formData.append('paymentInstructions', data.paymentInstructions);
    }
    if (data.showDesignStudioSubmitInstructions !== undefined) {
      formData.append('showDesignStudioSubmitInstructions', String(data.showDesignStudioSubmitInstructions));
    }
    if (data.designStudioSubmitInstructions !== undefined) {
      formData.append('designStudioSubmitInstructions', data.designStudioSubmitInstructions);
    }
    if (data.showLayoutCustomizationSubmitInstructions !== undefined) {
      formData.append('showLayoutCustomizationSubmitInstructions', String(data.showLayoutCustomizationSubmitInstructions));
    }
    if (data.layoutCustomizationSubmitInstructions !== undefined) {
      formData.append('layoutCustomizationSubmitInstructions', data.layoutCustomizationSubmitInstructions);
    }
    if (data.homeOwnersGuideProjects !== undefined) {
      formData.append('homeOwnersGuideProjects', JSON.stringify(data.homeOwnersGuideProjects));
    }
    if (data.colorSelectionProjects !== undefined) {
      formData.append('colorSelectionProjects', JSON.stringify(data.colorSelectionProjects));
    }
    if (data.amenitiesButtonProjects !== undefined) {
      formData.append('amenitiesButtonProjects', JSON.stringify(data.amenitiesButtonProjects));
    }
    if (data.projectProgressButtonProjects !== undefined) {
      formData.append('projectProgressButtonProjects', JSON.stringify(data.projectProgressButtonProjects));
    }
    if (data.closingGuideButtonProjects !== undefined) {
      formData.append('closingGuideButtonProjects', JSON.stringify(data.closingGuideButtonProjects));
    }

    // Add images to delete
    if (data.imagesToDelete !== undefined && data.imagesToDelete.length > 0) {
      formData.append('imagesToDelete', JSON.stringify(data.imagesToDelete));
    }

    // Add file fields if they exist
    if (data.headerLogo) {
      formData.append('headerLogo', data.headerLogo);
    }
    if (data.loginPageLogo) {
      formData.append('loginPageLogo', data.loginPageLogo);
    }
    if (data.loginPageHeroImage) {
      formData.append('loginPageHeroImage', data.loginPageHeroImage);
    }
    if (data.bannerImage) {
      formData.append('bannerImage', data.bannerImage);
    }
    if (data.incentiveImage) {
      formData.append('incentiveImage', data.incentiveImage);
    }

    const response = await api.post(
      `/workspaces/${workspaceId}/portal-settings`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },
};
